import React from 'react';
import { pushDataLayerEvent } from '../utils/analytics';
import './SliderInput.scss';

interface SliderInputProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  unit: string;
  unitPosition?: 'before' | 'after';
  prefix?: string;
  readOnly?: boolean;
  wide?: boolean;
  onChange?: (value: number) => void;
  formatValue?: (value: number) => string;
  /** Stable identifier for analytics (e.g. 'weekday_km'), falls back to `label` if omitted */
  trackingId?: string;
}

const SliderInput: React.FC<SliderInputProps> = ({
  label,
  value,
  min,
  max,
  step,
  unit,
  unitPosition = 'after',
  prefix = '',
  readOnly = false,
  wide = false,
  onChange,
  formatValue,
  trackingId,
}) => {
  const displayValue = formatValue ? formatValue(value) : value.toString();
  const percentage = ((value - min) / (max - min)) * 100;
  const fillBorderRadius = percentage >= 100 ? '4px' : '4px 0 0 4px';
  const valueText =
    (unitPosition === 'before'
      ? `${unit} ${displayValue}`
      : `${displayValue} ${unit}`)
      .replace(/\s+/g, ' ')
      .trim();

  return (
    <div className={`slider-input${readOnly ? ' slider-input--readonly' : ''}${wide ? ' slider-input--wide' : ''}`}>
      <div className="slider-input__header">
        <span className="slider-input__label">{label}</span>
        <span className="slider-input__value">
          {unitPosition === 'before' && (
            <span className="slider-input__unit slider-input__unit--before">{unit}</span>
          )}
          {prefix && <span className="slider-input__prefix">{prefix}</span>}
          <span className="slider-input__number">{displayValue}</span>
          {unitPosition === 'after' && <span className="slider-input__unit">{unit}</span>}
        </span>
      </div>
      {!readOnly && (
        <div className="slider-input__track-container">
          <div
            className="slider-input__track-fill"
            style={{ width: `${percentage}%`, borderRadius: fillBorderRadius }}
          />
          <div
            className="slider-input__track-unfilled"
            style={{ width: `${100 - percentage}%` }}
          />
          <input
            type="range"
            className="slider-input__range"
            min={min}
            max={max}
            step={step}
            value={value}
            aria-label={label}
            aria-valuetext={valueText}
            onChange={(e) => {
              const nextValue = parseFloat(e.target.value);
              onChange?.(nextValue);
              pushDataLayerEvent('calculator_slider_interaction', {
                slider_label: label,
                slider_id: trackingId ?? label,
                slider_value: nextValue,
                slider_min: min,
                slider_max: max,
              });
            }}
          />
        </div>
      )}
    </div>
  );
};

export default SliderInput;
